import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FiLink, FiShoppingBag, FiBarChart, FiShare, FiCheck, FiEye, FiEdit2, FiSliders } from 'react-icons/fi';
import { createBreakpoint } from 'react-use';

import { removeUrlProtocol } from '@mssgme/helpers';
import { useBilling, useCopyText, useSwitchApp, useWorkspace } from '@mssgme/hooks';
import { ScrollableBox, Nav, NavItemLink, useModal, Button } from '@mssgme/ui';
import { FreeBanner } from '@mssgme/app/components/UI';
import { PreviewModal, PageLayout } from '../../../UI';
import iconQr from '../../../img/qr.svg';

import styles from './PageMain.scss';

const useBreakpoint = createBreakpoint({ mobile: 0, tablet: 768, desktop: 1280 });
const SWITCH_TO = 'next';

export default function PageMain() {
    const breakpoint = useBreakpoint();
    const { t, i18n } = useTranslation('cards');
    const { isCopied, copyText } = useCopyText();
    const switchApp = useSwitchApp();

    const { card } = useWorkspace();
    const modal = useModal();
    const { isFree, isUnpaid, isTrial } = useBilling();

    const url = card.profile.absoluteUrl;

    const onSwitch = (e) => {
        e.preventDefault();
        switchApp(SWITCH_TO);
    };

    return (
        <PageLayout>
            <ScrollableBox noHeader nopadding>
                <div className={styles.personalLinkContainer}>
                    <h2>{t('CardMain.personal_link')}</h2>
                    <div className={styles.userLinkContainer}>
                        <a href={url} target="_blank" rel="noopener noreferrer" className={styles.userLink}>
                            <span className={styles.elToSelect}>{url}</span>
                            {removeUrlProtocol(url)}
                        </a>
                        {breakpoint === 'mobile' && (
                            <span onClick={modal.toggle} className={styles.previewBtn}>
                                <FiEye size={20} color="#000" />
                            </span>
                        )}
                    </div>
                    <div className={styles.topNav}>
                        <div
                            style={{ pointerEvents: isCopied ? 'none' : 'auto' }}
                            className={cn({
                                [styles.topNavItem]: true,
                                [styles.topNavItemEn]: i18n.language === 'en',
                                [styles.copied]: isCopied,
                            })}
                            onClick={() => copyText(url)}
                        >
                            <div className={styles.topNavItemIcon}>
                                {!isCopied && <FiLink size={24} color="#000" />}
                                {isCopied && <FiCheck className={styles.checkIcon} size={24} color="#fff" />}
                            </div>
                            <span className={styles.topNavItemLabel}>
                                {isCopied ? t('CardMain.copied') : t('CardMain.copyLink')}
                            </span>
                        </div>
                        <Link
                            className={cn({ [styles.topNavItem]: true, [styles.topNavItemEn]: i18n.language === 'en' })}
                            to="/p/share/qr"
                        >
                            <div className={styles.topNavItemIcon}>
                                <img src={iconQr} alt="" />
                            </div>
                            <span className={styles.topNavItemLabel}>{t('CardMain.qrCode')}</span>
                        </Link>
                        <Link
                            className={cn({ [styles.topNavItem]: true, [styles.topNavItemEn]: i18n.language === 'en' })}
                            to="/p/share"
                        >
                            <div className={styles.topNavItemIcon}>
                                <FiShare size={24} color="#000" />
                            </div>
                            <span className={styles.topNavItemLabel}>{t('common:labels.share')}</span>
                        </Link>
                    </div>
                </div>
                <div className={styles.navContainer}>
                    <Nav>
                        <NavItemLink to="/p/edit" icon={FiEdit2}>
                            {t('CardMain.pageEdit')}
                        </NavItemLink>
                        <NavItemLink to="/p/products" icon={FiShoppingBag}>
                            {t('CardMain.products')}
                        </NavItemLink>
                        <NavItemLink to="/p/analytics" icon={FiBarChart}>
                            {t('CardMain.analytics')}
                        </NavItemLink>
                        <NavItemLink to="/p/settings" icon={FiSliders}>
                            {t('CardMain.pageSettings')}
                        </NavItemLink>
                    </Nav>
                </div>
                <div style={{ padding: '0 16px', marginBottom: 20 }}>
                    <Button
                        fullWidth
                        outline
                        style={{
                            border: '1px solid #2979FF',
                            borderRadius: 8,
                            backgroundColor: 'rgba(41, 121, 255, 0.05)',
                            fontWeight: 700,
                            fontSize: 16,
                        }}
                        onClick={onSwitch}
                    >
                        {t('CardMain.new_version')}
                    </Button>
                </div>
                {(isFree || isUnpaid || isTrial) && <FreeBanner />}
            </ScrollableBox>
            <PreviewModal isShowing={modal.isOpen} toggle={modal.toggle} pageUrl={url} />
        </PageLayout>
    );
}
